import React, { useContext, useState, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Draggable, Droppable } from 'react-beautiful-dnd';

import {
  Typography,
  IconButton,
  Collapse,
  List,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  SvgIcon,
  Grow,
  CircularProgress,
  Popover,
  TextField
} from '@material-ui/core';
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import AddCircleIcon from '@material-ui/icons/AddCircle';
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { withStyles } from '@material-ui/core/styles';
import ViewObjectCard from 'components/explore/views/ViewObjectCard';
import NameInput from 'components/explore/views/NameInput';
import { setExploreSelectedObjectId, deleteExploreViewLayer, createExploreViewObject, setConfirmationModalState,
  updateSelectedExploreView, setExploreLastCreatedLayerId, handleExploreViewObjectVisibilityChange, createExploreViewLayer } from 'store/actions';
import { Constants } from 'utilities/constants';
import { viewObjects } from 'store/explore/views';
import { createGUID } from 'utilities';
import { sendClientLog } from 'services/logService';
import { extendBoundsToObject } from 'components/explore/gmap/helpers';
import { MapRefContext } from 'components/explore/Explore';
import { fitBoundsWithZoom } from 'components/explore/gmap/helpers';

const Layer = ({ classes, layer, selectedView, objectCountLimitOverrides, setObjectCountLimitOverrides, setExploreSelectedObjectId,
  createExploreViewLayer, deleteExploreViewLayer, createExploreViewObject, setConfirmationModalState, updateSelectedExploreView,
  lastCreatedLayerId, setExploreLastCreatedLayerId, handleExploreViewObjectVisibilityChange, index, hasDataObjectLoading }) => {
  const mapRef = useContext(MapRefContext);
  const layerRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState(null);
  const [addObjectMenuAnchorEl, setAddObjectMenuAnchorEl] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [objectCardMenuIsOpen, setObjectCardMenuIsOpen] = useState(false);
  const [countOverrideType, setCountOverrideType] = useState(null);
  const [countOverrideAnchor, setCountOverrideAnchor] = useState(null);
  const [countOverrideInput, setCountOverrideInput] = useState('');
  const [countOverrideInputError, setCountOverrideInputError] = useState(false);
  const dragDisabled = moreMenuAnchorEl || addObjectMenuAnchorEl || objectCardMenuIsOpen;

  useEffect(() => { // Effect to control mount animations- none for existing layers. Newly added layers will scroll into view, then display a mount transition
    if (!layerRef.current || layer.id !== lastCreatedLayerId) return;
    let scrollTimeout;
    const checkForEnd = function(e) { // Debounced check for end of scroll event
      clearTimeout(scrollTimeout); // Reset the timer each time a scroll event fires
      scrollTimeout = setTimeout(function() {
        setExploreLastCreatedLayerId(null); // Once scrolling is done, call action to start the mount animation
          document.getElementById('viewContent').removeEventListener('scroll', checkForEnd);
      }, 100);
    }
    document.getElementById('viewContent').addEventListener('scroll', checkForEnd);
    layerRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    scrollTimeout = setTimeout(function() { // If added layer is in view and no scroll occurs, this clears the listener and displays the layer
      document.getElementById('viewContent').removeEventListener('scroll', checkForEnd);
      setExploreLastCreatedLayerId(null);
    }, 100);
  }, [lastCreatedLayerId]);

  const layerObjects = useMemo(() => {
    return layer.objectIds.map(objectId => {
      return selectedView.objects.find(object => object.id === objectId);
    });
  }, [layer.objectIds, selectedView.objects]);

  const handleObjectClick = (objectId) => {
    setExploreSelectedObjectId(objectId);
    sendClientLog('Info',`Explore Views - User opened object detail for object id ${objectId}, layer id ${layer.id}, view id ${selectedView.id}`);
  }

  const isVisible = layerObjects.length === 0 || layerObjects.some(object => object.visible);
  const handleLayerVisibilityChange = () => {
    handleExploreViewObjectVisibilityChange(layer.objectIds, !isVisible);
    sendClientLog('Info',`Explore Views - User toggled visibility to ${!isVisible} for layer id ${layer.id}, view id ${selectedView.id}`);
  }

  const openMoreMenu = (e) => {
    setMoreMenuAnchorEl(e.currentTarget);
  }

  const closeMoreMenu = () => {
    setMoreMenuAnchorEl(null);
  }

  const handleBestFitLayer = (e, ignoreVisibility = false) => { // When using fit and focus, extend bounds even for invisible objects as they will be set to visible shortly after
    closeMoreMenu();
    let bounds = new window.google.maps.LatLngBounds();
    layerObjects.forEach(obj => {
      if ((!ignoreVisibility && !obj.visible) || obj.needsPositionSet) return;
      bounds = extendBoundsToObject(bounds, obj, mapRef);
    })
    mapRef?.current && fitBoundsWithZoom(mapRef.current, bounds);
    sendClientLog('Info',`Explore Views - User used best fit for layer id ${layer.id}, view id ${selectedView.id}`);
  }

  const handleFocusLayer = () => {
    closeMoreMenu();
    handleExploreViewObjectVisibilityChange(layer.objectIds, true, true);
    sendClientLog('Info',`Explore Views - User used focus for layer id ${layer.id}, view id ${selectedView.id}`);
  }

  const handleFitAndFocusLayer = (e) => {
    closeMoreMenu();
    handleBestFitLayer(e, true);
    handleFocusLayer();
  }

  const handleRenameClick = (timeout = 0) => {
    closeMoreMenu();
    setTimeout(() => setIsEditingName(true), timeout);
  }

  const viewObjCounts = useMemo(() => {
    return selectedView.objects.reduce((counts, obj) => {
      counts[obj.type] = (counts[obj.type] ?? 0) + 1;
      return counts;
    }, {});
  }, [selectedView.objects]);

  const dupWouldExceedLimit = useMemo(() => {
    const layerObjCounts = layerObjects.reduce((counts, obj) => {
      counts[obj.type] = (counts[obj.type] ?? 0) + 1;
      return counts;
    }, {});
    const hasValueExceeding = Object.keys(layerObjCounts).some(type => {
      const limit = Constants.ViewsObjectCountLimit[type];
      // console.log(type, limit, layerObjCounts[type], viewObjCounts[type])
      return (layerObjCounts[type] + viewObjCounts[type]) > limit;
    })

    return hasValueExceeding;
  }, [layerObjects, viewObjCounts]);

  const handleDuplicateLayer = () => {
    closeMoreMenu();
    createExploreViewLayer('Copy of ' + layer.name, layer.id);
    sendClientLog('Info',`Explore Views - User duplicated layer id ${layer.id}, view id ${selectedView.id}`);
  }

  const handleDeleteLayer = () => {
    closeMoreMenu();
    if (!layerObjects.length) {
      deleteExploreViewLayer(layer.id);
      sendClientLog('Info',`Explore Views - User deleted layer with id ${layer.id} and 0 objects for view id ${selectedView.id}`);
    } else {
      setConfirmationModalState({
        isModalOpen: true,
        handleConfirmation: () => {
          closeConfirmationModal();
          deleteExploreViewLayer(layer.id);
          sendClientLog('Info',`Explore Views - User deleted layer with id ${layer.id} and ${layerObjects.length} objects for view id ${selectedView.id}`);
        },
        handleCancellation: () => {
          closeConfirmationModal();
        },
        titleText: 'Confirm',
        contentText: `Are you sure you want to delete this layer? All objects within it will also be deleted.`
      })
    }
  }
  const closeConfirmationModal = () => {
    setConfirmationModalState({ isModalOpen: false });
  }

  const openAddObjectMenu = (e) => {
    setAddObjectMenuAnchorEl(e.currentTarget);
  }

  const closeAddObjectMenu = () => {
    setAddObjectMenuAnchorEl(null);
  }

  const handleAddObject = type => {
    closeAddObjectMenu();
    createExploreViewObject('tempObject-' + createGUID(), layer.id, type);
    sendClientLog('Info',`Explore Views - User added object with type ${type} to layer id ${layer.id}, view id ${selectedView.id}`);
  }

  const handleNameSave = name => {
    setIsEditingName(false);
    if (name === layer.name) return;
    updateSelectedExploreView({ layers: selectedView.layers.map(el => {
      if (el.id === layer.id) {
        return {
          ...el,
          name
        }
      }
      return el;
    })});
  }

  const toggleCountOverridePopover = currentTarget => {
    const el = countOverrideAnchor ? null : currentTarget;
    setCountOverrideAnchor(el);
  }

  const handleCountOverride = (e, type, objectLimit) => {
    e.preventDefault();
    setCountOverrideInput(objectLimit);
    setCountOverrideInputError(null);
    setCountOverrideType(type);
    toggleCountOverridePopover(e.currentTarget);
  }

  const handleCountOverrideInputChange = e => {
    setCountOverrideInput(e.target.value);
    const isValidInt = /^-?\d+$/.test(e.target.value.trim());
    if (!isValidInt) {
      setCountOverrideInputError('Invalid value');
    } else {
      const parsed = parseInt(e.target.value);
      if (parsed < 0 || parsed > 100) {
        setCountOverrideInputError('Value must be between 0 and 100');
      } else {
        setCountOverrideInputError(null);
      }
    }
  }

  const saveCountOverride = (type) => {
    const initialValue = objectCountLimitOverrides?.[type] ?? Constants.ViewsObjectCountLimit[type];
    setObjectCountLimitOverrides({
      ...objectCountLimitOverrides,
      [type]: countOverrideInput
    });
    toggleCountOverridePopover();
    sendClientLog('Info',`Explore Views - User overrode ${type} object limit from ${initialValue} to ${countOverrideInput} for view id ${selectedView.id}`);
  }

  return (
    <Draggable key={`layer-${layer.id}`} draggableId={`layer-${layer.id}`} index={index} isDragDisabled={dragDisabled}>
      {
        (provided) => (
          <Grow
            in={layer.id !== lastCreatedLayerId}
            appear={false}
            timeout={300}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            ref={provided.innerRef}
          >
            <div ref={layerRef} className={classes.layerContainer}>
              <div className={classes.header}>
                <div className={classes.headerLeft}>
                  <IconButton
                    className={classes.expandButton}
                    style={{transform: `rotate(${isExpanded ? '0' : '-90'}deg)`,}}
                    onClick={() => setIsExpanded(!isExpanded)}
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                  {isEditingName
                    ? <>
                      <NameInput name={layer.name} onSave={handleNameSave} charLimit={Constants.ViewsCharLimit.LAYERNAME} />
                      {/* <IconButton
                    className={classes.moreButton}
                    onClick={openMoreMenu}
                  >
                    <MoreHorizIcon />
                  </IconButton> */}
                    </>
                    : <>
                      <Typography variant='subtitle2' className={classes.name} onDoubleClick={handleRenameClick}>{layer.name}</Typography>
                      <IconButton
                        className={classes.moreButton}
                        onClick={openMoreMenu}
                      >
                        <MoreHorizIcon />
                      </IconButton>
                      <div className={classes.loadingSpacer} style={{ marginLeft: 4 }}>
                        {!isExpanded && hasDataObjectLoading
                          ? <CircularProgress size={24} />
                          : <div className={classes.loadingSpacer}/>
                        }
                      </div>
                    </>
                  }
                  <Menu
                    anchorEl={moreMenuAnchorEl}
                    open={Boolean(moreMenuAnchorEl)}
                    onClose={closeMoreMenu}
                    anchorOrigin={{
                      horizontal: 'right',
                      vertical: 'bottom'
                    }}
                  >
                    <MenuItem onClick={handleBestFitLayer} disabled={layerObjects.every(obj => !obj.visible || obj.needsPositionSet == null || obj.needsPositionSet)}>Fit Layer on map</MenuItem>
                    <MenuItem onClick={handleFitAndFocusLayer} disabled={layerObjects.every(obj => obj.needsPositionSet == null || obj.needsPositionSet)}>Fit and focus Layer</MenuItem>
                    <MenuItem onClick={handleFocusLayer}>Focus Layer</MenuItem>
                    <MenuItem onClick={() => handleRenameClick(100)}>Rename Layer</MenuItem>
                    <Tooltip title={dupWouldExceedLimit ? 'One or more objects would exceed quantity limit' : ''}>
                      <span>
                        <MenuItem onClick={handleDuplicateLayer} disabled={dupWouldExceedLimit}>Duplicate Layer</MenuItem>
                      </span>
                    </Tooltip>
                    <MenuItem onClick={handleDeleteLayer}>Delete Layer</MenuItem>
                  </Menu>
                </div>
                <div className={classes.headerRight}>
                  <Tooltip title='Add Object'>
                    <IconButton
                      className={classes.visibilityButton}
                      onClick={openAddObjectMenu}
                    >
                      <AddCircleIcon />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={addObjectMenuAnchorEl}
                    open={Boolean(addObjectMenuAnchorEl)}
                    onClose={closeAddObjectMenu}
                    anchorOrigin={{
                      horizontal: 'right',
                      vertical: 'bottom'
                    }}
                  >
                    {Object.keys(viewObjects).map(type => {
                      const objectCount = selectedView.objects.filter(object => object.type === type).length;
                      const objectLimit = objectCountLimitOverrides?.[type] ?? Constants.ViewsObjectCountLimit[type];
                      const countLimitReached = objectCount >= objectLimit;
                      const obj = viewObjects[type];
                      return <Tooltip key={type} title={countLimitReached ? 'Quantity limit reached (double click to override)' : ''}>
                          <span onDoubleClick={countLimitReached ? (e) => handleCountOverride(e, type, objectLimit) : () => { }}>
                            <MenuItem
                            disabled={countLimitReached || obj.disabled}
                            onClick={() => handleAddObject(type)}
                          >
                            <ListItemIcon style={{ minWidth: 40 }}>
                              <SvgIcon><path d={obj.icon} /></SvgIcon>
                            </ListItemIcon>
                            <Typography variant='inherit'>{obj.displayType}</Typography>
                          </MenuItem>
                        </span>
                      </Tooltip>
                    })}
                  </Menu>
                  {countOverrideType &&
                    <Popover
                      open={Boolean(countOverrideAnchor)}
                      anchorEl={countOverrideAnchor}
                      onClose={toggleCountOverridePopover}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                      }}
                    >
                      <div className={classes.overrideContainer}>
                        <Typography>{`Enter a new limit for ${viewObjects[countOverrideType].displayType} objects:`}</Typography>
                        <div>
                          <TextField
                            value={countOverrideInput}
                            onChange={handleCountOverrideInputChange}
                            error={countOverrideInputError != null}
                            helperText={countOverrideInputError ?? null}
                          />
                          <IconButton color="primary" size='small' style={{ marginTop: 4 }} disabled={countOverrideInputError != null} onClick={() => saveCountOverride(countOverrideType)}>
                            <CheckCircleIcon />
                          </IconButton>
                        </div>
                      </div>
                    </Popover>
                  }
                  <Tooltip title='Toggle layer visibility'>
                    <IconButton
                      className={classes.visibilityButton}
                      onClick={handleLayerVisibilityChange}
                      disabled={!layerObjects.length}
                    >
                      {isVisible
                        ? <VisibilityIcon />
                        : <VisibilityOffIcon />
                      }
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
              <Droppable
                droppableId={`object-${layer.id}`}
                type="OBJECT"
                direction="vertical"
                isDropDisabled={!isExpanded}
              >
                {
                  (provided) => (
                    <Collapse in={isExpanded}>
                    <List
                      className={classes.openedList}
                      dense
                      id={layer.id}
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {layerObjects.length
                        ? layerObjects.map((object, index) => {
                          return <ViewObjectCard
                            key={object.id}
                            object={object}
                            setObjectCardMenuIsOpen={setObjectCardMenuIsOpen}
                            handleObjectClick={handleObjectClick}
                            index={index}
                            objectCount={viewObjCounts[object.type]}
                          />
                        })
                        : <div className={classes.noObjectsDiv}><Typography className={classes.noObjectsText} variant='body2'>Layer contains no objects</Typography></div>
                      }
                      {provided.placeholder}
                    </List>
                    </Collapse>
                  )
                  }
                </Droppable>
            </div>
          </Grow>
        )
      }
    </Draggable>
  )
}

const styles = {
  layerContainer: {
    scrollMargin: '50px'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '2px 4px',
    backgroundColor: '#cbcbcb',
    borderBottom: '1px solid #b7b7b7',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    // margin: '8px 0',
    minWidth: 0
  },
  headerRight: {
    marginLeft: 4,
    display: 'flex'
  },
  name: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  openedList: {
    paddingTop: 5,
    paddingBottom: 5,
    backgroundColor: '#f2f2f2',
    transition: 'background-color 0.2 ease',
    overflowY: 'auto',
    borderBottom: '1px solid #b7b7b7'
  },
  expandButton: {
    transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    padding: 2,
    marginRight: 4
  },
  visibilityButton: {
    padding: 2
  },
  moreButton: {
    padding: 2,
    marginLeft: 4
  },
  editNameInputRoot: {
    fontSize: '.875rem',
    fontWeight: 500,
    width: 250
  },
  editNameInput: {
    paddingBottom: 4
  },
  noObjectsDiv: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 42,
    margin: '4px 8px'
  },
  noObjectsText: {
    fontStyle: 'italic',
    color: '#a7a9ac',
    fontSize: '.9rem'
  },
  loadingSpacer: {
    height: 24,
    width: 24
  },
  overrideContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 8
  }
}

const mapStateToProps = (state, ownProps) => {
  const { exploreViews, exploreData, exploreShapesData } =  state;
  const pdoIdsLoading = Object.keys(exploreData.propertyDataObjects).filter(key => {
    const pdo = exploreData.propertyDataObjects[key];
    return pdo.isMainIndicatorVisible;
  });
  const sdoIdsLoading = Object.keys(exploreShapesData.shapesDataObjects).filter(key => {
    const pdo = exploreShapesData.shapesDataObjects[key];
    return pdo.isLoading;
  });
  return {
    selectedView: exploreViews.selectedView,
    lastCreatedLayerId: exploreViews.lastCreatedLayerId,
    hasDataObjectLoading: pdoIdsLoading.concat(sdoIdsLoading).some(id => ownProps.layer.objectIds.map(id => id.toString()).includes(id))
  }
};

const mapDispatchToProps = {
  setExploreSelectedObjectId,
  createExploreViewLayer,
  deleteExploreViewLayer,
  createExploreViewObject,
  setConfirmationModalState,
  updateSelectedExploreView,
  setExploreLastCreatedLayerId,
  handleExploreViewObjectVisibilityChange
};

export default compose(
  withStyles(styles),
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(Layer);
